/**
 * GamePage Component
 *
 * Main game container page. Replaces the old RoomPage.js.
 * Orchestrates all game phases and manages shared game state.
 *
 * Used by:
 * - Router (replaces RoomPage route)
 *
 * Responsibilities:
 * - Initialize game connection (socket, room joining)
 * - Manage overall game state
 * - Render appropriate phase component based on current phase
 * - Handle game-wide UI (disconnect timer, modals, etc.)
 * - Coordinate between phases
 *
 * Phase Rendering:
 * - Phase 0/LOBBY: LobbyPhase
 * - Phase 1/ISLAND_PLACEMENT: IslandPlacementPhase
 * - Phase 2/MONSTER_PLACEMENT: MonsterPlacementPhase
 * - Phase 3/ACTIVE: GameplayPhase
 * - Phase 4/ENDED: GameOverPhase
 *
 * Replaces: pages/RoomPage.js (will be broken down into this and phase components)
 */

import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../AuthProvider';
import { getAuthToken } from '../utils/authToken';
import useGameSocket from '../game/hooks/useGameSocket';
import { useGameStateContext } from '../game/context/useGameStateContext';
import useGamePhase from '../game/hooks/useGamePhase';
import { MonsterStateProvider } from '../game/context/MonsterStateContext';
import { LobbyPhase } from '../game/phases/LobbyPhase';
import { IslandPlacementPhase } from '../game/phases/IslandPlacementPhase';
import { MonsterPlacementPhase } from '../game/phases/MonsterPlacementPhase';
import { GameplayPhase } from '../game/phases/GameplayPhase';
import { GameOverPhase } from '../game/phases/GameOverPhase';
import type { PlayerColor } from '../game/types/game.types';
import type { PlayerDeductionsDocument } from '../game/types/monster.types';
import type { JoinRoomResponse } from '../game/types/socket.types';
import Loading from '../components/loading';
import { DisconnectTimer } from '../game/components/UI/DisconnectTimer';
import '../game/game.css';
/**
 * GamePage Component
 */
export const GamePage: React.FC = () => {
    const { roomCode } = useParams<{ roomCode: string }>();
    const navigate = useNavigate();
    const { user } = useAuth();
    const socket = useGameSocket();
    const { onReconnected } = socket;

    // Store socket in ref to avoid dependency issues
    const socketRef = useRef(socket);
    socketRef.current = socket; // Update ref on each render

    // Game state hooks
    const gameState = useGameStateContext();
    const { theme, disconnectTimer, getPlayer, phase } = gameState;
    const disconnectedPlayer = disconnectTimer.disconnectedPlayer
        ? getPlayer(disconnectTimer.disconnectedPlayer)
        : null;
    const disconnectedPlayerName = disconnectedPlayer?.username ?? null;
    const { isLobby, isIslandPlacement, isMonsterPlacement, isActive, isEnded } = useGamePhase();

    // Loading state
    const [isJoining, setIsJoining] = useState(true);
    const [joinError, setJoinError] = useState<string | null>(null);
    /** Hydrates MonsterStateProvider from join / reconnect when a game exists */
    const [joinPlayerDeductions, setJoinPlayerDeductions] = useState<
        PlayerDeductionsDocument | null | undefined
    >(undefined);

    // Track if we've initiated connection to prevent duplicate attempts
    const hasInitiatedConnection = useRef<string | null>(null);

    // Get room code from URL or params
    const currentRoomCode = roomCode || window.location.pathname.slice(1);

    // Connect and join room on mount – only after token exists (user or guest JWT from 3.1)
    useEffect(() => {
        if (!currentRoomCode || !user) {
            setJoinError('Invalid room code or user not authenticated');
            setIsJoining(false);
            hasInitiatedConnection.current = null;
            return;
        }
        if (!getAuthToken()) {
            setJoinError('No session found, please wait or refresh');
            setIsJoining(false);
            hasInitiatedConnection.current = null;
            return;
        }

        // Prevent duplicate connection attempts for the same room
        if (hasInitiatedConnection.current === currentRoomCode) {
            return;
        }

        // Reset flag if room code changed
        if (hasInitiatedConnection.current && hasInitiatedConnection.current !== currentRoomCode) {
            hasInitiatedConnection.current = null;
        }

        // Mark that we've initiated connection for this room
        hasInitiatedConnection.current = currentRoomCode;

        setIsJoining(true);
        setJoinError(null);
        setJoinPlayerDeductions(undefined);

        // Use preferred color from user settings when available (e.g. logged-in user);
        // guests or missing settings use red as default
        const preferredColor: PlayerColor = user.settings?.color
            ? (user.settings.color as PlayerColor)
            : 'red';

        const titleFileName = user.guest
            ? 'Cadet'
            : (user.settings?.title?.fileName?.trim() || 'Cadet');

        // Connect to socket and join room in one atomic operation
        socketRef.current.connectAndJoinRoom(
            {
                roomName: currentRoomCode,
                username: user.username,
                color: preferredColor,
                titleFileName,
            },
            {
                timeout: 30000, // 30 second timeout
                maxRetries: 3, // Retry up to 3 times on failure
            }
        )
            .then((response) => {
                setIsJoining(false);

                // Set player letter from response
                if (response.playerLetter) {
                    gameState.setMyPlayerLetter(response.playerLetter);
                }

                // Update room data if provided
                if (response.room) {
                    gameState.setRoom(response.room);
                }

                // When joining a room with an active game (e.g. reconnect), apply game state and phase
                if (response.gameState) {
                    gameState.setGameState(response.gameState);
                    gameState.setPhase(response.gameState.phase);
                    setJoinPlayerDeductions(response.playerDeductions ?? null);
                } else {
                    setJoinPlayerDeductions(undefined);
                }
            })
            .catch((error) => {
                setIsJoining(false);
                setJoinError(error.message || 'Failed to connect and join room');
                // Reset connection flag on error so we can retry if needed
                hasInitiatedConnection.current = null;
                // Redirect to home after a delay
                setTimeout(() => {
                    navigate('/home');
                }, 2000);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentRoomCode, user, navigate]);

    // Handle cleanup on unmount
    useEffect(() => {
        return () => {
            // Leave room when component unmounts, then disconnect (only if connected and joined)
            if (currentRoomCode && socketRef.current.isConnected) {
                socketRef.current.leaveRoom(
                    { roomName: currentRoomCode },
                    () => {
                        socketRef.current.disconnect();
                    }
                );
            }
        };
    }, [currentRoomCode]);

    // Handle window unload
    useEffect(() => {
        const handleBeforeUnload = (): void => {
            if (currentRoomCode && socketRef.current.isConnected) {
                socketRef.current.leaveRoom(
                    {
                        roomName: currentRoomCode,
                    },
                    () => {
                        // Cleanup handled
                    }
                );
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [currentRoomCode]);

    useEffect(() => {
        return onReconnected((response: JoinRoomResponse) => {
            if (response.success && response.gameState) {
                setJoinPlayerDeductions(response.playerDeductions ?? null);
            }
        });
    }, [onReconnected]);

    // Show loading while joining
    if (isJoining) {
        return <Loading text="Joining room..." />;
    }

    // Show error if join failed
    if (joinError) {
        return (
            <div style={{ padding: '20px', textAlign: 'center' }}>
                <h2>Error joining room</h2>
                <p>{joinError}</p>
                <p>Redirecting to home...</p>
            </div>
        );
    }

    // Render phase-specific component (MonsterStateProvider so useMonsters/useBoard share monster state)
    return (
        <MonsterStateProvider
            key={currentRoomCode}
            initialDeductionsDocument={
                joinPlayerDeductions === undefined ? undefined : joinPlayerDeductions
            }
        >
            <div className="game_page_container" data-theme={theme}>
                <DisconnectTimer
                    active={disconnectTimer.active}
                    secondsRemaining={disconnectTimer.secondsRemaining}
                    disconnectedPlayerName={disconnectedPlayerName}
                />
                {/* Render appropriate phase component */}
                {isLobby && <LobbyPhase roomCode={currentRoomCode} />}

                {isIslandPlacement && <IslandPlacementPhase roomCode={currentRoomCode} />}

                {isMonsterPlacement && <MonsterPlacementPhase roomCode={currentRoomCode} />}

                {isActive && <GameplayPhase roomCode={currentRoomCode} />}

            {isEnded && <GameOverPhase />}

                {/* Show unknown phase if somehow we get an invalid phase */}
                {!isLobby && !isIslandPlacement && !isMonsterPlacement && !isActive && !isEnded && (
                    <div style={{ padding: '20px', textAlign: 'center' }}>
                        <h2>Unknown Phase</h2>
                        <p>Current phase: {phase}</p>
                    </div>
                )}
            </div>
        </MonsterStateProvider>
    );
};

export default GamePage;
